import type { LeafletMouseEvent, Map, MapOptions } from "leaflet";
import { lazy, Suspense, useState } from "react";
import type { FC, ReactNode } from "react";
import type { MapEventsProps } from "./leaflet-mapevents-hooks";
import type {
  LeafletLocationSearchProps,
  OSMRes,
} from "./leaflet-location-search";
import { MapContext } from "./leaflet-context";

const LazyLeafletMap = lazy(() => import("./leaflet-base-map"));
const LazyMapEvents = lazy<FC<MapEventsProps>>(
  () => import("./leaflet-mapevents-hooks"),
);
const LazyLocationSearch = lazy<FC<LeafletLocationSearchProps>>(
  () => import("./leaflet-location-search"),
);

const LazyMapConsumer = lazy<FC<{ setMap: (m: Map) => void }>>(
  () => import("./leaflet-map-consumer"),
);

const LazyMarkerClusterGroup = lazy<FC<any>>(
  () => import("react-leaflet-markercluster"),
);
const LazyMarker = lazy(() => import("./leaflet-marker"));

export const LeafletMap: FC<
  {
    center: [number, number];
    markers: [number, number][];
    children?: ReactNode;
    onClick?: (map: Map, e: LeafletMouseEvent) => void;
    onLocationSearchSelect?: (map: Map, res: OSMRes) => void;
  } & MapOptions
> = ({
  center,
  markers,
  onClick,
  children,
  onLocationSearchSelect,
  ...options
}) => {
  const [map, setMap] = useState<Map | null>(null);

  return (
    <Suspense fallback={<div className="h-[400px]" />}>
      <MapContext.Provider value={{ map, setMap }}>
        {children}
        {onLocationSearchSelect && (
          <>
            <LazyLocationSearch onSelect={onLocationSearchSelect} />
            <div className="mb-2" />
          </>
        )}

        <LazyLeafletMap center={center} zoom={13} {...options}>
          <Suspense fallback={<></>}>
            <LazyMapEvents onClick={onClick} />

            <LazyMarkerClusterGroup>
              {markers.map((position: [number, number], index: number) => (
                <LazyMarker key={index} position={position} />
              ))}
            </LazyMarkerClusterGroup>
            <LazyMapConsumer setMap={setMap} />
          </Suspense>
        </LazyLeafletMap>
      </MapContext.Provider>
    </Suspense>
  );
};
